import React, { useContext } from 'react';
import type { InteractionPart } from '@lib/models/InteractionPart';
import type { Interaction } from '@lib/models/Interaction';
import { AiChatServiceContext } from '../../AiChatContext';
import AskUserPart from './AskUserPart';
import AskAuthPart from './AskAuthPart';
import type { PartRendererProps } from './types';
import { ASK_AUTH_COMPONENT, askUserComponentOf } from './types';
import { parseClientOutput, parseUiArgs, type UiToolContext } from './uiToolRegistry';

export interface AskUserDispatcherProps extends Partial<PartRendererProps> {
    /** The ask-user function part (only field required, so the drawer can render it standalone). */
    part: InteractionPart;
    /** Drop the card chrome — set by the docked AskUserSheet. */
    embedded?: boolean;
}

/**
 * The generic ask-user renderer. Every ask-user part (server-flagged
 * `functionDetails.isAskUser` via `[KernelUIComponent]`, or legacy ask_user/askauth by name) routes
 * here; this dispatcher resolves the named component (`functionDetails.askUserComponent`) against the
 * host's `uiTools` registry `askUser` entries and draws it. The library's own `AskUserPart`
 * (questionnaire) and `AskAuthPart` (sign-in) are the built-in fallbacks when the host hasn't
 * registered the component — so `AskCreateAccount` (host-registered) and the built-ins share one path.
 *
 * Mirrors {@link UiToolDispatcher} for the `uiTool` kind. Used both inline (the locked transcript card
 * once answered) and, with `embedded`, inside the docked {@link AskUserSheet} while parked.
 */
const AskUserDispatcher: React.FC<AskUserDispatcherProps> = (props) => {
    const { part, message, onGoing = false, preview = false, embedded } = props;
    const services = useContext(AiChatServiceContext);
    const fd = part.functionDetails;
    const componentName = askUserComponentOf(fd);

    // A host-registered ask-user component (e.g. AskCreateAccount) wins over the built-ins.
    const entry = services?.uiTools?.resolveAskUser(componentName);
    if (entry) {
        const args = entry.parse ? entry.parse(fd?.args) : parseUiArgs(fd?.args);
        const ctx: UiToolContext = {
            name: entry.name,
            callId: fd?.id ?? '',
            response: fd?.response,
            clientOutput: parseClientOutput(fd?.clientOutput),
            parts: message?.parts ?? [],
            message: (message ?? { parts: [] }) as Interaction,
            onGoing,
            preview,
        };
        // Two faces: the interactive `question` while parked (in the docked drawer, `embedded`), and the
        // locked `response` summary inline once answered (status 'Done'). A host that registered only a
        // `question` reuses it in both states (it owns its own answered view, like the built-ins).
        const answered = fd?.status === 'Done';
        if (!embedded && answered && entry.response) {
            return <>{entry.response(args ?? {}, ctx, fd?.response)}</>;
        }
        return <>{entry.question(args ?? {}, ctx)}</>;
    }

    // Built-in fallbacks (also the default registrations): sign-in vs questionnaire.
    if (componentName === ASK_AUTH_COMPONENT) return <AskAuthPart part={part} embedded={embedded} />;
    return <AskUserPart part={part} embedded={embedded} />;
};

export default AskUserDispatcher;
